import React, { useCallback, useEffect, useRef, useState } from "react";
import { MainContainer } from "@components/templates/layouts/main-container/main-container";
import { RoundBorderComponentContainer } from "@components/templates/layouts/round-border-component-container/round-border-component-container";
import styles from "./tickets-page.module.scss";
import { SubHeader } from "@src/components/atoms/sub-header/sub-header";
import { ContainerPanel } from "@src/components/organisms/container-panel/container-panel";
import { ContainerPane } from "@src/components/organisms/container-pane/container-pane";
import { SupportAgentTicket } from "@src/types/support";
import { useSelector } from "react-redux";
import { RootState } from "@src/reducers/store";
import EmptyTicketsInboxTicketsPage from "@src/components/molecules/empty-tickets-inbox-tickets-page/empty-tickets-inbox-tickets-page";
import EmptyTicketContainerTicketsPage from "@src/components/molecules/empty-ticket-container-tickets-page/empty-ticket-container-tickets-page";
import { SubTitle } from "@src/components/atoms/sub-title/sub-title";
import { useI18N } from "@src/language/language-context";
import refreshBlueIcon from "@src/assets/svg/refresh-blue.svg";

import Spinner from "@src/components/atoms/spinner/spinner";
import { TicketsList } from "@src/components/organisms/tickets-list/tickets-list";
import { SelectedTicketView } from "@src/components/organisms/selected-ticket-view/selected-ticket-view";
import { FlexRowContainer } from "@src/components/templates/layouts/flex-row-container/flex-row-container";
import { IconButton } from "@src/components/atoms/buttons/icon-button/icon-button";
import { getSupportTicketsWithLanguageUseCase } from "@src/cloud-connect/support/use-cases/get-support-tickets-with-language-use-case";
import { errorSnackbar } from "@src/cloud-connect/snackbars/entities/snackbar-launcher";

const cssClassName = "tickets-page";

export const TicketsPage = () => {
  const i18n = useI18N();
  const permissions = useSelector(
    (state: RootState) => state.yourSlice.authContext?.permissions,
  );
  const lastEvent = useSelector(
    (state: RootState) =>
      state.sseSlice.streams?.["service-support"]?.["lastEvent"],
  );
  const lastEventAt = useSelector(
    (state: RootState) =>
      state.sseSlice.streams?.["service-support"]?.["lastEventAt"],
  );

  const [isLoadingTickets, setIsLoadingTickets] = useState<boolean>(true);
  const [isLoadingOnlyTicktetsList, setIsLoadingOnlyTicketsList] =
    useState<boolean>(false);
  const [selectedTicket, setSelectedTicket] =
    useState<SupportAgentTicket | null>(null);
  const [tickets, setTickets] = useState<SupportAgentTicket[]>([]);

  const selectedTicketRef = useRef<SupportAgentTicket | null>(null);
  const language = useSelector((state: RootState) => state.yourSlice.language);
  const languageChangeRef = useRef(language);

  useEffect(() => {
    selectedTicketRef.current = selectedTicket;
  }, [selectedTicket]);

  const texts = {
    inboxTitle: i18n("pages.ticketsPage.inboxTitle"),
    refreshText: i18n("pages.ticketsPage.refreshText"),
    errorFetchingTickets: i18n("pages.ticketsPage.errorFetchingTickets"),
  };

  const handleRefreshTickets = async () => {
    setIsLoadingOnlyTicketsList(true);
    const result = await getSupportTicketsWithLanguageUseCase(language);

    if (result.success && result.data) {
      setTickets(result.data.tickets);
    } else {
      console.error("Failed to fetch tickets:", result.error);
      errorSnackbar(result.error || texts.errorFetchingTickets);
    }
    setIsLoadingOnlyTicketsList(false);
  };

  useEffect(() => {
    // waiting for permissions to be ready before making any call
    if (!permissions) {
      return;
    }

    const fetchTickets = async () => {
      setIsLoadingTickets(true);
      const result = await getSupportTicketsWithLanguageUseCase(language);

      if (result.success && result.data) {
        setTickets(result.data.tickets);
      } else {
        console.error("Failed to fetch tickets:", result.error);
        errorSnackbar(result.error || texts.errorFetchingTickets);
      }
      setIsLoadingTickets(false);
    };
    fetchTickets();
  }, [permissions]);

  const fetchOnEvent = useCallback(async () => {
    const result = await getSupportTicketsWithLanguageUseCase(language);
    if (result.success && result.data) {
      setTickets(result.data.tickets);
    }
  }, [language]);

  useEffect(() => {
    if (!permissions || isLoadingTickets) return;

    if (
      lastEvent === "ticket_updated" ||
      lastEvent == "ticket_created" ||
      lastEvent == "comment_created" ||
      languageChangeRef.current != language
    )
      fetchOnEvent();

    languageChangeRef.current = language;
  }, [
    lastEvent,
    lastEventAt,
    fetchOnEvent,
    permissions,
    isLoadingTickets,
    language,
  ]);

  return (
    <MainContainer>
      <RoundBorderComponentContainer>
        <div className={styles[cssClassName]}>
          <div className={styles[`${cssClassName}__inner-container`]}>
            {isLoadingTickets ? (
              <Spinner />
            ) : (
              <>
                <div className={styles[`${cssClassName}__column`]}>
                  <ContainerPanel>
                    <SubHeader variant={"less-padding"}>
                      <FlexRowContainer justifyContent="space-between">
                        <SubTitle text={texts.inboxTitle} />
                        <IconButton
                          fun={handleRefreshTickets}
                          icon={refreshBlueIcon}
                          tooltip={texts.refreshText}
                          tooltipPosition="left"
                        />
                      </FlexRowContainer>
                    </SubHeader>
                    <ContainerPane>
                      <div className={styles[`${cssClassName}__pane-content`]}>
                        {tickets && tickets.length > 0 ? (
                          <>
                            {isLoadingOnlyTicktetsList ? (
                              <Spinner />
                            ) : (
                              <TicketsList
                                tickets={tickets}
                                onSelectTicket={setSelectedTicket}
                                selectedTicketId={selectedTicket?.id}
                              />
                            )}
                          </>
                        ) : (
                          <EmptyTicketsInboxTicketsPage />
                        )}
                      </div>
                    </ContainerPane>
                  </ContainerPanel>
                </div>

                {tickets && tickets.length > 0 && selectedTicket ? (
                  <SelectedTicketView
                    key={selectedTicket.id}
                    selectedTicket={selectedTicket}
                  />
                ) : (
                  <EmptyTicketContainerTicketsPage />
                )}
              </>
            )}
          </div>
        </div>
      </RoundBorderComponentContainer>
    </MainContainer>
  );
};
